import type { ServiceStatus, StatusKind, StatusSnapshot } from '@/types/status';

const VERCEL_STATUS_URL = 'https://www.vercel-status.com/api/v2/status.json';

// Gemini ping endpoint — minimal model, low cost.
const GEMINI_API_URL =
  'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent';

interface VercelStatusResponse {
  status: {
    indicator: 'none' | 'minor' | 'major' | 'critical';
    description: string;
  };
}

function mapIndicatorToKind(indicator: VercelStatusResponse['status']['indicator']): StatusKind {
  if (indicator === 'none') return 'operational';
  if (indicator === 'minor') return 'degraded';
  return 'outage';
}

export async function fetchVercelStatus(timeoutMs = 5000): Promise<ServiceStatus> {
  const fallback: ServiceStatus = {
    id: 'deployment',
    name: 'Deployment',
    description: 'Vercel edge deployment and CDN.',
    status: 'operational',
    source: 'mock'
  };

  try {
    const controller = new AbortController();
    const timerId = window.setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(VERCEL_STATUS_URL, { signal: controller.signal });
    window.clearTimeout(timerId);

    if (!response.ok) return fallback;

    const data: VercelStatusResponse = (await response.json()) as VercelStatusResponse;
    const now = new Date().toISOString();

    return {
      id: 'deployment',
      name: 'Deployment',
      description: 'Vercel edge deployment and CDN.',
      status: mapIndicatorToKind(data.status.indicator),
      source: 'api',
      lastChecked: now
    };
  } catch {
    return fallback;
  }
}

/**
 * Ping the Gemini API with a minimal request to check availability.
 * Uses VITE_GEMINI_API_KEY from the client environment.
 * Falls back to 'unknown' with a disclaimer when the key is absent.
 * Called at StatusPage mount and on manual refresh — never auto-polled.
 */
export async function pingGemini(timeoutMs = 5000): Promise<ServiceStatus> {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;

  const unknownFallback: ServiceStatus = {
    id: 'ai',
    name: 'AI infrastructure',
    description: 'Gemini-powered panel generation and debate engine.',
    status: 'operational',
    source: 'mock',
    disclaimer: 'Configuration health-check Gemini absente'
  };

  const errorFallback: ServiceStatus = {
    id: 'ai',
    name: 'AI infrastructure',
    description: 'Gemini-powered panel generation and debate engine.',
    status: 'degraded',
    source: 'api',
    lastChecked: new Date().toISOString()
  };

  if (!apiKey) {
    return unknownFallback;
  }

  try {
    const controller = new AbortController();
    const timerId = window.setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: 'ping' }] }]
      })
    });

    window.clearTimeout(timerId);
    const now = new Date().toISOString();

    if (response.ok) {
      return {
        id: 'ai',
        name: 'AI infrastructure',
        description: 'Gemini-powered panel generation and debate engine.',
        status: 'operational',
        source: 'api',
        lastChecked: now
      };
    }

    // Non-2xx but not a timeout: degraded
    return { ...errorFallback, lastChecked: now };
  } catch {
    // Timeout or network error
    return { ...errorFallback, status: 'outage', lastChecked: new Date().toISOString() };
  }
}

export function fetchGeminiStatus(): ServiceStatus {
  // Kept for backward compatibility — prefers pingGemini() for real health checks.
  return {
    id: 'ai',
    name: 'AI infrastructure',
    description: 'Gemini-powered panel generation and debate engine.',
    status: 'operational',
    source: 'mock'
  };
}

export async function fetchStatusSnapshot(): Promise<StatusSnapshot> {
  const [gemini, vercel] = await Promise.all([pingGemini(), fetchVercelStatus()]);

  return {
    services: [gemini, vercel],
    fetchedAt: new Date().toISOString()
  };
}
